import { AxiosError } from "axios";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button, SubmitButton } from "@/components/Interface/Button/Button";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { errorShouldBeRetried } from "@/components/Portal/UserAccount/Security/utils";
import { NameConfirmationField } from "@/components/Welcome/AuthenticatorSetup/NameConfirmationField";
import { useAlert } from "@/hooks/useAlert";
import { portalRoutes } from "@/router/routes/portalRoutes";
import {
  useCancelSetup,
  useChangeTwoFactorAuthentication,
} from "@/services/auth";
import { ChangeTwoFactorAuthenticationMethod } from "@/services/model";
import { AlertTypes } from "@/utils/atoms";

type ConfirmStandardForm = {
  name: string;
};
export const TwoFactorConfirmStandard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<ConfirmStandardForm>({
    mode: "onChange",
  });

  const { mutateAsync: cancelSetup, isLoading: isCancelingSetup } =
    useCancelSetup();
  const { mutateAsync: changeTwoFactorAuth } =
    useChangeTwoFactorAuthentication();
  const showToast = useAlert();

  const isSubmitting = useRef(false);

  const onSubmit = () => {
    if (isSubmitting.current) {
      return;
    }
    isSubmitting.current = true;

    const handleSuccess = () => {
      showToast({
        text: t("portal:user-account.security.2fa.confirmStandard.success"),
        type: AlertTypes.success,
      });
      navigate(portalRoutes.cockpit.base);
    };

    const tryToChange = () =>
      changeTwoFactorAuth({
        params: { method: ChangeTwoFactorAuthenticationMethod.STANDARD },
      });

    tryToChange()
      .then(handleSuccess)
      .catch((error: AxiosError<any>) => {
        if (errorShouldBeRetried(error)) {
          cancelSetup().then(() => {
            setTimeout(() => tryToChange().then(handleSuccess), 5000);
          });
        } else {
          showToast({
            text: t("portal:user-account.security.2fa.confirmStandard.error"),
            type: AlertTypes.error,
          });
        }
      })
      .finally(() => {
        isSubmitting.current = false;
      });
  };

  return (
    <>
      {isCancelingSetup && <LoadingSpinner />}
      <form onSubmit={handleSubmit(onSubmit)}>
        <ProfileCard
          maxWidth="none"
          title={t("portal:user-account.security.2fa.confirmStandard.title")}
          actions={
            <>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => navigate(-1)}
              >
                {t("portal:user-account.security.buttons.cancel")}
              </Button>
              <SubmitButton
                fullWidth={false}
                size="sm"
                disabled={isCancelingSetup}
                label={t("common.buttons.confirm")}
              />
            </>
          }
        >
          <p className="text-base font-semibold text-foreground-primary">
            {t("portal:user-account.security.2fa.confirmStandard.subtitle")}
          </p>
          <p className="text-base text-foreground-primary">
            {t("portal:user-account.security.2fa.confirmStandard.text1")}
          </p>
          <p className="text-base text-foreground-primary">
            {t("portal:user-account.security.2fa.confirmStandard.text2")}
          </p>
          <p className="text-base text-foreground-primary">
            {t("portal:user-account.security.2fa.confirmStandard.text3")}
          </p>
          <NameConfirmationField
            register={register}
            errors={errors}
            title={t(
              "portal:user-account.security.2fa.confirmStandard.steps.2.title",
            )}
          />
        </ProfileCard>
      </form>
    </>
  );
};
